import { useCallback, useEffect } from "react";
import { useQuery, UseQueryResult } from "react-query";
import { atom, useRecoilState } from "recoil";

import { getCurrentBanner } from "../Layout/utils";
import { getCookie, setCookie } from "../services/utils";
import { BannerType } from "./interfaces";

// Dismissal state is shared because useBanner() is mounted in more than one
// layout at once (Layout + DataAssistantLayout on /data-assistant routes).
// Local useState would let the instances diverge, so closing the banner in one
// wouldn't hide it in the other (it would reappear when navigating back to a
// non-full-bleed route). A single atom keeps every instance in sync.
const bannerClosedAtom = atom<boolean>({
  key: "bannerClosedAtom",
  default: false,
});

// Whether the current banner is eligible to show (no dismiss cookie for it yet).
// Shared for the same reason as bannerClosedAtom.
const bannerVisibleAtom = atom<boolean>({
  key: "bannerVisibleAtom",
  default: false,
});

/**
 * Shared info-banner logic (fetch, cookie-based dismissal, visibility state).
 * Consumed by the layouts that need to display the banner so the query/cookie
 * handling lives in one place. React Query dedupes the ["infoBanner"] key and
 * the visibility/dismissal state lives in Recoil, so mounting the hook from
 * several layouts results in a single request and one shared source of truth.
 */
export const useBanner = () => {
  const [closeBanner, setCloseBanner] = useRecoilState(bannerClosedAtom);
  const [showBanner, setShowBanner] = useRecoilState(bannerVisibleAtom);

  const bannerData: UseQueryResult<BannerType | null, unknown> = useQuery(
    ["infoBanner"],
    () => getCurrentBanner(),
    { refetchInterval: 300_000, refetchIntervalInBackground: false }
  );

  const handleCloseBanner = useCallback(() => {
    if (bannerData.data) {
      const banner: BannerType = bannerData.data;
      const nbDaysBeforeRedisplay = banner.nb_days_before_redisplay;
      // 0 means "never redisplay" — use a large expiry since 0 days would expire the cookie immediately
      const cookieDays =
        nbDaysBeforeRedisplay === 0 ? 365 : nbDaysBeforeRedisplay;

      setCookie(
        "revbell-bannerExpiration",
        `${nbDaysBeforeRedisplay}--${banner.id}`,
        cookieDays
      );
      setCloseBanner(true);
    }
  }, [bannerData.data, setCloseBanner]);

  useEffect(() => {
    if (bannerData.data) {
      const banner = bannerData.data;
      const getCookieBanner = getCookie("revbell-bannerExpiration");

      if (!getCookieBanner) {
        setShowBanner(true);
      } else {
        const bannerId = getCookieBanner.split("--")[1];
        if (Number(bannerId) !== banner.id) {
          document.cookie =
            "revbell-bannerExpiration=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
          setShowBanner(true);
        }
      }
    }
  }, [bannerData.data, setShowBanner]);

  return {
    banner: bannerData.data ?? null,
    isVisible: Boolean(bannerData.data) && showBanner && !closeBanner,
    handleCloseBanner,
  };
};
